import { memo, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import {
  trackCompareGridEvents,
  CompareGridLocations,
  CompareGridEventNames,
} from '@utils/gtm/ga4/trackCompareGridEvents';
import { useTranslation } from 'next-i18next';
import { useLocale } from '@hooks/useLocale';
import { ArrowLeftIcon, ArrowRightIcon } from '@phosphor-icons/react';
import DOMPurify from 'isomorphic-dompurify';

import IconButton from '_components/UI/IconButton';
import { MecProduct, PriceGroup } from '@adapters/bigcommerce/types/product';
import { MultiGroupPrices } from '@adapters/bigcommerce/operations/getAllCustomerGroupPrices';
import useProduct from '@adapters/bigcommerce/hooks/product/useProduct';
import { transformProductPrice } from '@utils/comparison/transformProductPrice';
import {
  AttributeType,
  getAttributeType,
  getCompareSpecArray,
  formatList,
} from '@utils/comparison/getCompareAttribute';
import { isCompareProductAvailable } from '@utils/comparison/isCompareProductAvailable';
import {
  getVisibleCompareItems,
  isProductCompareTableShown,
} from '@utils/comparison/productCompareColumns';
import { usePrice } from '@hooks/usePrice';
import { slugify } from '@utils/url';

import CompareHeading, { hasHeading } from '../CompareHeading';
import ProductColumn from './columns/ProductColumn';
import LoadingSkeleton from './LoadingSkeleton';
import {
  CompareAttribute,
  CompareItem,
  CompareLocale,
  ProductCompareTableProps,
} from './types';
import LinkButton from '_components/UI/LinkButton';

import styles from './ProductCompareTable.module.scss';

const FIXED_ATTRS: CompareAttribute[] = [
  { key: 'name', label: { en: 'Name', fr: 'Nom' } },
  { key: 'price', label: { en: 'Price', fr: 'Prix' } },
];

const LOCATION = CompareGridLocations.ProductCompare;

// An attribute is identified by its code or either of its labels, since merch
// can name a spec however they like in Akeneo.
const getAttributeTypes = (attr: CompareAttribute): AttributeType[] =>
  [attr.key, attr.label?.en, attr.label?.fr].map((key) =>
    getAttributeType(key ?? '')
  );

const getLocalized = (
  text: Partial<Record<CompareLocale, string>> | undefined,
  locale: CompareLocale
): string => text?.[locale]?.trim() ?? '';

const ProductCompareTable = ({
  surface,
  product,
  config,
  groupPricesByCode,
}: ProductCompareTableProps) => {
  const { asPath } = useRouter();
  const path = asPath.split('?')[0];
  const { t } = useTranslation('comparison');
  const activeLocale = useLocale();
  const locale: CompareLocale = activeLocale === 'fr' ? 'fr' : 'en';
  const formatPrice = usePrice();
  const { getProduct } = useProduct();
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);
  const hasTrackedScroll = useRef(false);

  const [bcProductsByCode, setBcProductsByCode] = useState<
    Map<string, MecProduct>
  >(new Map());
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const items = useMemo(() => config.items ?? [], [config.items]);
  const { contextLink, heading, productTypeLabel } = config;
  const contextLinkTarget =
    contextLink?.openInNewTab === false ? undefined : '_blank';

  const isHiddenBySeed = !isCompareProductAvailable(product);

  const itemCodes = useMemo(
    () =>
      isHiddenBySeed
        ? []
        : items.map((item) => item?.id).filter((id): id is string => !!id),
    [items, isHiddenBySeed]
  );

  const visibleItems = useMemo(
    () => getVisibleCompareItems(items, bcProductsByCode),
    [items, bcProductsByCode]
  );

  const seedIndex = useMemo(
    () =>
      product?.code
        ? visibleItems.findIndex((item) => item?.id === product.code)
        : -1,
    [visibleItems, product?.code]
  );

  // buildProductCompareConfig moves the seed to the front, so the first column
  // is the emphasized one.
  const emphasizedIndex = 0;

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const handleScroll = () => {
      updateScrollState();
      if (!hasTrackedScroll.current) {
        hasTrackedScroll.current = true;
        trackCompareGridEvents({
          eventName: CompareGridEventNames.ScrollCompareTable,
          interactionLocation: LOCATION,
          path,
        });
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      ro.disconnect();
    };
  }, [updateScrollState, isLoadingProducts, visibleItems.length, path]);

  const handleSeedNameClick = useCallback(
    (
      e: React.MouseEvent<HTMLAnchorElement>,
      linkName: string,
      href: string,
      index: number
    ) => {
      const atc = document.getElementById('add-to-cart');
      if (!atc) return;
      trackCompareGridEvents({
        eventName: CompareGridEventNames.ClickCompareTable,
        interactionLocation: LOCATION,
        linkUrl: href,
        linkText: linkName,
        index,
        path,
      });
      e.preventDefault();
      atc.scrollIntoView({ behavior: 'smooth', block: 'end' });
    },
    [path]
  );

  const scrollByColumn = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    const table = tableRef.current;
    if (!el) return;
    const firstProductHeader = table?.querySelector<HTMLElement>(
      'thead tr th:nth-child(2)'
    );
    const colWidth = firstProductHeader?.offsetWidth ?? 304;
    el.scrollBy({
      left: direction === 'left' ? -colWidth : colWidth,
      behavior: 'smooth',
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableElement>) => {
      const table = tableRef.current;
      if (!table) return;

      const focusable = Array.from(
        table.querySelectorAll<HTMLElement>(
          'td[tabindex="0"], th[tabindex="0"], a, button, [tabindex="0"]'
        )
      );
      const idx = focusable.findIndex((el) => el === document.activeElement);
      if (idx === -1) return;

      let next = idx;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          next = Math.min(idx + 1, focusable.length - 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          next = Math.max(idx - 1, 0);
          break;
        case 'ArrowDown':
        case 'ArrowUp': {
          e.preventDefault();
          const cell = focusable[idx];
          const row = cell.closest('tr');
          if (!row) return;
          const allRows = Array.from(
            table.querySelectorAll('tbody tr, thead tr')
          );
          const rowIdx = allRows.indexOf(row);
          const delta = e.key === 'ArrowDown' ? 1 : -1;
          const nextRow = allRows[rowIdx + delta];
          if (nextRow) {
            const colIdx = Array.from(row.children).indexOf(
              cell as HTMLElement
            );
            const nextCell = nextRow.children[colIdx] as HTMLElement;
            if (nextCell) {
              const focusableInCell =
                nextCell.querySelector<HTMLElement>(
                  'a, button, [tabindex="0"]'
                ) ?? (nextCell.hasAttribute('tabindex') ? nextCell : null);
              focusableInCell?.focus();
            }
          }
          return;
        }
        case 'Home':
          e.preventDefault();
          next = 0;
          break;
        case 'End':
          e.preventDefault();
          next = focusable.length - 1;
          break;
        default:
          return;
      }

      if (next !== idx) focusable[next]?.focus();
    },
    []
  );

  const handleTableClick = ({
    linkName,
    href,
    index,
  }: {
    linkName: string;
    href: string;
    index: number | null;
  }) => {
    trackCompareGridEvents({
      eventName: CompareGridEventNames.ClickCompareTable,
      interactionLocation: LOCATION,
      linkUrl: href,
      linkText: linkName,
      index,
      path,
    });
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true;
          trackCompareGridEvents({
            eventName: CompareGridEventNames.ViewCompareTable,
            interactionLocation: LOCATION,
            path,
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // isLoadingProducts in deps ensures the effect re-runs once the container
    // div replaces the loading skeleton (containerRef.current is null before)
  }, [isLoadingProducts, path]);

  useEffect(() => {
    let cancelled = false;
    if (itemCodes.length === 0) {
      setBcProductsByCode(new Map());
      setIsLoadingProducts(false);
      return undefined;
    }
    setIsLoadingProducts(true);
    const fetchProducts = async () => {
      const fetched = await Promise.all(
        itemCodes.map((code) => getProduct(code))
      );
      if (cancelled) return;
      const next = new Map<string, MecProduct>();
      fetched.forEach((fetchedProduct, i) => {
        if (fetchedProduct) next.set(itemCodes[i], fetchedProduct);
      });
      setBcProductsByCode(next);
      setIsLoadingProducts(false);
    };
    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [getProduct, itemCodes]);

  const groupPricesMap = useMemo(() => {
    const map = new Map<string, MultiGroupPrices | null>();
    if (groupPricesByCode) {
      Object.entries(groupPricesByCode).forEach(([code, prices]) => {
        map.set(code, prices);
      });
    }
    return map;
  }, [groupPricesByCode]);

  const productPrices = useMemo(() => {
    const map = new Map<string, PriceGroup | null>();
    bcProductsByCode.forEach((bcProduct, code) => {
      map.set(code, transformProductPrice(bcProduct));
    });
    return map;
  }, [bcProductsByCode]);

  const displayPrices = useMemo(() => {
    const map = new Map<string, { value: number; isExact: boolean }>();
    productPrices.forEach((price, code) => {
      const exactPrice = price?.price?.value ?? null;
      const lowPriceValue = price?.lowPrice?.value ?? null;
      if (exactPrice != null) {
        map.set(code, { value: exactPrice, isExact: true });
      } else if (lowPriceValue != null) {
        map.set(code, { value: lowPriceValue, isExact: false });
      }
    });
    return map;
  }, [productPrices]);

  // Ideal-for values render under the product name, so the attribute never
  // gets a row of its own.
  const idealForAttr = useMemo(
    () =>
      (config.attributes ?? []).find((attr) =>
        getAttributeTypes(attr).includes('idealFor')
      ),
    [config.attributes]
  );

  const sortedAttributes = useMemo(() => {
    const rest = (config.attributes ?? []).filter((attr) => {
      const types = getAttributeTypes(attr);
      return (
        !types.includes('name') &&
        !types.includes('price') &&
        !types.includes('idealFor')
      );
    });
    return [...FIXED_ATTRS, ...rest];
  }, [config.attributes]);

  // Items can ship with an empty title; the catalog name of the product the
  // column resolves to is the same string merch would have typed.
  const getItemTitle = useCallback(
    (item: CompareItem): string =>
      getLocalized(item.title, locale) ||
      bcProductsByCode.get(item.id)?.name ||
      '',
    [bcProductsByCode, locale]
  );

  const buildItemHref = useCallback(
    (item: CompareItem, linksToBuyZone: boolean): string => {
      if (linksToBuyZone) return '#add-to-cart';
      const ctaUrl = getLocalized(item.cta?.url, locale);
      if (ctaUrl) return ctaUrl;
      const slugSource =
        bcProductsByCode.get(item.id)?.slug || getLocalized(item.title, locale);
      const basePath = `/${locale}/product/${item.id}`;
      return slugSource ? `${basePath}/${slugify(slugSource)}` : basePath;
    },
    [bcProductsByCode, locale]
  );

  if (isHiddenBySeed) return null;

  if (items.length === 0) return null;

  if (isLoadingProducts) {
    return <LoadingSkeleton />;
  }

  if (
    !isProductCompareTableShown({
      seedProduct: product,
      items,
      productsByCode: bcProductsByCode,
    })
  ) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${
        surface === 'category' ? styles.containerInSlice : ''
      }`}
    >
      <nav className={styles.navigation} aria-label={t('compare.nav.label')}>
        <IconButton
          icon={ArrowLeftIcon}
          type={canScrollLeft ? 'primary' : 'outline'}
          size="md"
          iconSize={24}
          iconWeight="light"
          shape="circular"
          disabled={!canScrollLeft}
          aria-label={t('compare.nav.previous')}
          onClick={() => scrollByColumn('left')}
        />
        <IconButton
          icon={ArrowRightIcon}
          type={canScrollRight ? 'primary' : 'outline'}
          size="md"
          iconSize={24}
          iconWeight="light"
          shape="circular"
          disabled={!canScrollRight}
          aria-label={t('compare.nav.next')}
          onClick={() => scrollByColumn('right')}
        />
      </nav>

      {hasHeading(heading) && (
        <CompareHeading heading={heading} className={styles.headingMobile} />
      )}

      <div ref={scrollRef} className={styles.scrollWrapper}>
        <table
          ref={tableRef}
          className={styles.table}
          aria-label={t('title')}
          role="grid"
          onKeyDown={handleKeyDown}
        >
          <caption className={styles.srOnly}>{t('title')}</caption>
          <colgroup>
            <col className={styles.labelCol} />
            {visibleItems.map((item, i) => (
              <col
                key={item.id}
                className={`${styles.productCol} ${
                  i === emphasizedIndex ? styles.firstProductCol : ''
                }`}
              />
            ))}
          </colgroup>

          <thead>
            <tr>
              <th
                scope="col"
                className={`${styles.labelCell} ${styles.labelHeaderCell}`}
              >
                {hasHeading(heading) ? (
                  <CompareHeading
                    heading={heading}
                    className={styles.heading}
                  />
                ) : (
                  <span className={styles.srOnly}>
                    {t('compare.attributes')}
                  </span>
                )}
                {contextLink && (
                  <LinkButton
                    variant="black"
                    href={contextLink.url}
                    target={contextLinkTarget}
                    onClick={() =>
                      handleTableClick({
                        linkName: contextLink.label,
                        href: contextLink.url,
                        index: null,
                      })
                    }
                  >
                    {contextLink.label}
                  </LinkButton>
                )}
              </th>
              {visibleItems.map((item, i) => {
                const linksToBuyZone = surface === 'pdp' && i === seedIndex;
                const title = getItemTitle(item);
                const productHref = buildItemHref(item, linksToBuyZone);
                return (
                  <th
                    key={item.id}
                    scope="col"
                    className={`${styles.productHeaderCell} ${
                      i === emphasizedIndex ? styles.pdpColumn : ''
                    }`}
                  >
                    <ProductColumn
                      name={title}
                      product={bcProductsByCode.get(item.id)}
                      imageUrl={item.image}
                      href={productHref}
                      onClick={
                        linksToBuyZone
                          ? (e) => handleSeedNameClick(e, title, productHref, i)
                          : () =>
                              handleTableClick({
                                linkName: title,
                                href: productHref,
                                index: i,
                              })
                      }
                      ariaLabel={title}
                      isFirst={i === emphasizedIndex}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {sortedAttributes.map((attr, attrIdx) => {
              const type = getAttributeType(attr.key);
              const isLastAttr = attrIdx === sortedAttributes.length - 1;
              const isNameRow = type === 'name';
              const attrLabel =
                (isNameRow && productTypeLabel) ||
                (attr.label?.[locale] ?? attr.key);

              return (
                <tr
                  key={attr.key}
                  className={`${styles.attrRow}${
                    isLastAttr ? ` ${styles.attrRowLast}` : ''
                  }`}
                >
                  <th scope="row" className={styles.labelCell}>
                    <span
                      className={
                        isNameRow && !productTypeLabel
                          ? styles.srOnly
                          : undefined
                      }
                    >
                      {attrLabel}
                    </span>
                  </th>
                  {visibleItems.map((item, i) => {
                    const linksToBuyZone = surface === 'pdp' && i === seedIndex;
                    let content: React.ReactNode;

                    if (type === 'name') {
                      const idealForConjunction = t('compare.ideal.for.and');
                      const idealForLabel = t('compare.ideal.for');
                      const rawSubtitle = item?.subtitle?.[locale];
                      const subtitleItems = (
                        Array.isArray(rawSubtitle)
                          ? rawSubtitle
                          : [rawSubtitle ?? '']
                      ).filter(Boolean);
                      const idealForItems =
                        (idealForAttr &&
                          getCompareSpecArray(
                            item,
                            idealForAttr.key,
                            locale
                          )) ||
                        subtitleItems;
                      const idealFor = idealForItems.length
                        ? formatList(idealForItems, idealForConjunction)
                        : null;
                      const hasIdealForLabel = !!idealFor
                        ?.toLocaleLowerCase(locale)
                        .startsWith(idealForLabel.toLocaleLowerCase(locale));
                      const title = getItemTitle(item);
                      const nameHref = buildItemHref(item, linksToBuyZone);
                      content = (
                        <div className={styles.nameWrapper}>
                          <a
                            href={nameHref}
                            className={styles.productNameLink}
                            onClick={
                              linksToBuyZone
                                ? (e) =>
                                    handleSeedNameClick(e, title, nameHref, i)
                                : () =>
                                    handleTableClick({
                                      linkName: title,
                                      href: nameHref,
                                      index: i,
                                    })
                            }
                          >
                            <span className={styles.productName}>{title}</span>
                          </a>
                          {idealFor && (
                            <span className={styles.idealFor}>
                              {hasIdealForLabel
                                ? idealFor
                                : `${idealForLabel} ${idealFor.toLocaleLowerCase(
                                    locale
                                  )}`}
                            </span>
                          )}
                        </div>
                      );
                    } else if (type === 'price') {
                      const displayPrice = displayPrices.get(item.id);
                      const formatted = displayPrice
                        ? formatPrice(displayPrice.value)
                        : null;
                      const groupPrices = groupPricesMap.get(item.id);
                      const guestLow =
                        groupPrices?.guest?.price?.lowPrice?.value;
                      const memberLow =
                        groupPrices?.member?.price?.lowPrice?.value;
                      const memberPrice =
                        guestLow != null &&
                        memberLow != null &&
                        guestLow > memberLow
                          ? guestLow - memberLow
                          : null;

                      content = formatted ? (
                        <>
                          <span>
                            {displayPrice?.isExact
                              ? formatted
                              : t('compare.from.price', { price: formatted })}
                          </span>
                          {memberPrice && (
                            <span className={styles.memberSavings}>
                              {t('compare.members.save')}
                            </span>
                          )}
                        </>
                      ) : (
                        <span>{t('compare.placeholder')}</span>
                      );
                    } else {
                      const values = getCompareSpecArray(
                        item,
                        attr.key,
                        locale
                      );
                      if (values && values.length > 1) {
                        content = (
                          <ul className={styles.attrList}>
                            {values.map((value) => (
                              <li
                                key={value}
                                className={styles.attrValue}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(value),
                                }}
                              />
                            ))}
                          </ul>
                        );
                      } else {
                        content = values ? (
                          <span
                            className={styles.attrValue}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(values[0]),
                            }}
                          />
                        ) : (
                          <span className={styles.placeholder}>
                            {t('compare.placeholder')}
                          </span>
                        );
                      }
                    }

                    return (
                      <td
                        key={`${item.id}-${attr.key}`}
                        className={`${styles.dataCell} ${
                          i === emphasizedIndex ? styles.pdpColumn : ''
                        }`}
                        tabIndex={0}
                        role="gridcell"
                      >
                        {type !== 'name' && (
                          <span className={styles.attrLabelMobile}>
                            {attrLabel}
                          </span>
                        )}
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {contextLink && (
        <div className={styles.contextLinkMobile}>
          <LinkButton
            variant="black"
            href={contextLink.url}
            target={contextLinkTarget}
            onClick={() =>
              handleTableClick({
                linkName: contextLink.label,
                href: contextLink.url,
                index: null,
              })
            }
          >
            {contextLink.label}
          </LinkButton>
        </div>
      )}
    </div>
  );
};

export default memo(ProductCompareTable);
